import "./App.css";
import "./index.css";
import "./bootstrap.css";
import React, { lazy, Suspense } from "react";
import { Route, Routes, useLocation, Outlet, Navigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProtectedRoute from "./components/ProtectedRoute";
import MainLayout from "./components/main-layout";

const Login                  = lazy(() => import("./pages/login"));
const NotFoundPage           = lazy(() => import("./pages/notfound"));
const Home                   = lazy(() => import("./pages/home"));
const Registration           = lazy(() => import("./pages/registration"));
const Appointment            = lazy(() => import("./pages/appointments"));
const Client                 = lazy(() => import("./pages/client"));
const Reviews                = lazy(() => import("./pages/reviews"));
const Invoice                = lazy(() => import("./pages/invoice"));
const Blogs                  = lazy(() => import("./pages/blogs"));
const ManageBlogs            = lazy(() => import("./pages/manageBlogs"));
const Notifications          = lazy(() => import("./pages/notifications"));
const ClientRegistration     = lazy(() => import("./pages/clientregistraion"));
const Leads                  = lazy(() => import("./pages/leads"));
const ImportClients          = lazy(() => import("./components/client/importClients"));
const TherapistRegistration  = lazy(() => import("./pages/therapistregistration"));
const Roles                  = lazy(() => import("./pages/roles"));
const AdminsPage             = lazy(() => import("./pages/admins"));
const EditProfile            = lazy(() => import("./pages/editProfile"));
const ResourcesPage          = lazy(() => import("./pages/resources"));
const IntakePage             = lazy(() => import("./pages/intake"));
const ChatUsersPage          = lazy(() => import("./pages/chatUsers"));
const EmailLogsPage          = lazy(() => import("./pages/emailLogs"));
const CertificatesPage       = lazy(() => import("./pages/certificates"));
const NewsletterPage         = lazy(() => import("./pages/newsletter"));
const ProbonoPage            = lazy(() => import("./pages/probono"));
const InternshipApplicationsPage = lazy(() => import("./pages/internship-applications"));
const TraineePortalPage = lazy(() => import("./pages/trainee-portal"));
const Client360Page = lazy(() => import("./pages/client-360"));
const SessionPacksPage = lazy(() => import("./pages/session-packs"));
const PackPricingPage = lazy(() => import("./pages/pack-pricing"));
const TodoPage = lazy(() => import("./pages/todo"));
const CollaborationsPage = lazy(() => import("./pages/collaborations"));
const SubscriptionsPage = lazy(() => import("./pages/subscriptions"));
const AssessmentPricingPage = lazy(() => import("./pages/assessmentPricing"));
const CouponsUpgradePage = lazy(() => import("./pages/couponsUpgrade"));
const OutreachEmailPage = lazy(() => import("./pages/outreachEmail"));
const TherapistCommsPage = lazy(() => import("./pages/therapistComms"));
const TherapistPayoutsPage = lazy(() => import("./pages/therapistPayouts"));
const ReportsPage            = lazy(() => import("./pages/reports"));
const QuotationsPage         = lazy(() => import("./pages/quotations"));
const OfferLettersPage       = lazy(() => import("./pages/offer-letters"));
const LetterheadsPage        = lazy(() => import("./pages/letterheads"));
const Reception              = lazy(() => import("./pages/reception"));
const TherapyRoomPage        = lazy(() => import("./pages/therapy-room"));
const LeadActivityPage       = lazy(() => import("./pages/leadActivity"));
const NoidaAppointmentsPage  = lazy(() => import("./pages/noidaAppointments"));
const NoidaReceptionPage     = lazy(() => import("./pages/noidaReception"));
const StaffPage              = lazy(() => import("./pages/staff"));

const theme = createTheme();

const ContentLoader = () => (
  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"60vh" }}>
    <div style={{ position:"relative", width:40, height:40 }}>
      <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:"3px solid #e2e8f0" }} />
      <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:"3px solid transparent", borderTopColor:"#1e7a4c", animation:"cyt-spin 0.7s linear infinite" }} />
    </div>
    <style>{`@keyframes cyt-spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

function FadeContent({ children }) {
  const { pathname } = useLocation();
  // opening a therapist (/therapists/:id) stays on the same page, so the list isn't rebuilt and reloaded
  const key = pathname.startsWith("/therapists") ? "/therapists" : pathname;
  return <React.Fragment key={key}>{children}</React.Fragment>;
}

/* Persistent layout route: MainLayout (sidebar + title bar) mounts once and
   stays mounted across every navigation underneath it — only the routed
   page inside <Outlet/> unmounts/remounts, so switching pages never blanks
   out or reloads the sidebar/topbar. */
function AppLayout() {
  return (
    <MainLayout>
      <Suspense fallback={<ContentLoader />}>
        <FadeContent>
          <Outlet />
        </FadeContent>
      </Suspense>
    </MainLayout>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<ContentLoader />}>
        <Routes>
          <Route path="/"      element={<Login />} />
          <Route path="/login" element={<Login />} />

          {/* Standalone kiosk page — no sidebar/topbar, so it can be left open
              full-screen at a reception desk. Still behind the same auth +
              permission gate as everything else, just outside AppLayout. */}
          <Route path="/noida-reception" element={<ProtectedRoute permKey="noidaCenter"><NoidaReceptionPage /></ProtectedRoute>} />

          <Route element={<AppLayout />}>
            <Route path="/home"                  element={<ProtectedRoute>                         <Home />                  </ProtectedRoute>} />
            {/* one route (id optional) so opening a therapist keeps the list mounted — no reload, opens instantly */}
            <Route path="/therapists/:id?"       element={<ProtectedRoute permKey="therapists">    <Registration />          </ProtectedRoute>} />
            <Route path="/clients"               element={<ProtectedRoute permKey="clients">       <Client />                </ProtectedRoute>} />
            <Route path="/reception"             element={<ProtectedRoute permKey="clients">       <Reception />             </ProtectedRoute>} />
            <Route path="/reception/:id"         element={<ProtectedRoute permKey="clients">       <Reception />             </ProtectedRoute>} />
            <Route path="/therapy-room"          element={<ProtectedRoute superAdminOnly>          <TherapyRoomPage />       </ProtectedRoute>} />
            <Route path="/appointments"          element={<ProtectedRoute permKey="bookings">      <Appointment />           </ProtectedRoute>} />
            <Route path="/leads"                 element={<ProtectedRoute permKey="leads">         <Leads />                 </ProtectedRoute>} />
            <Route path="/newsletter"            element={<ProtectedRoute permKey="leads">         <NewsletterPage />        </ProtectedRoute>} />
            <Route path="/import-clients"        element={<ProtectedRoute permKey="clients">       <ImportClients />         </ProtectedRoute>} />
            <Route path="/reviews"               element={<ProtectedRoute permKey="reviews">       <Reviews />               </ProtectedRoute>} />
            <Route path="/invoices"              element={<ProtectedRoute permKey="invoices">      <Invoice />               </ProtectedRoute>} />
            <Route path="/blogs"                 element={<ProtectedRoute permKey="blogs">         <Blogs />                 </ProtectedRoute>} />
            <Route path="/manage-blogs"          element={<ProtectedRoute permKey="blogs">         <ManageBlogs />           </ProtectedRoute>} />
            <Route path="/notifications"         element={<ProtectedRoute permKey="notifications"> <Notifications />         </ProtectedRoute>} />
            <Route path="/roles"                 element={<ProtectedRoute superAdminOnly>          <Roles />                 </ProtectedRoute>} />
            <Route path="/admins"                element={<ProtectedRoute superAdminOnly>          <AdminsPage />            </ProtectedRoute>} />
            <Route path="/lead-activity"         element={<ProtectedRoute superAdminOnly>          <LeadActivityPage />      </ProtectedRoute>} />
            <Route path="/noida-appointments"    element={<ProtectedRoute permKey="noidaCenter">   <NoidaAppointmentsPage /> </ProtectedRoute>} />
            <Route path="/collaborations"        element={<ProtectedRoute permKey="noidaCenter">   <CollaborationsPage />    </ProtectedRoute>} />
            <Route path="/subscriptions"         element={<ProtectedRoute permKey="therapists">    <SubscriptionsPage />     </ProtectedRoute>} />
            <Route path="/assessment-pricing"    element={<ProtectedRoute permKey="therapists">    <AssessmentPricingPage /> </ProtectedRoute>} />
            <Route path="/coupons-upgrade"       element={<ProtectedRoute permKey="therapists">    <CouponsUpgradePage /> </ProtectedRoute>} />
            <Route path="/outreach-email"        element={<ProtectedRoute permKey="outreach">      <OutreachEmailPage /> </ProtectedRoute>} />
            <Route path="/therapist-payouts"     element={<ProtectedRoute superAdminOnly>          <TherapistPayoutsPage />  </ProtectedRoute>} />
            <Route path="/therapist-comms"       element={<ProtectedRoute permKey="therapists">    <TherapistCommsPage />    </ProtectedRoute>} />
            <Route path="/collaborations/:id"    element={<ProtectedRoute permKey="noidaCenter">   <CollaborationsPage />    </ProtectedRoute>} />
            <Route path="/team"                  element={<ProtectedRoute permKey="staff">         <StaffPage />             </ProtectedRoute>} />
            <Route path="/staff"                 element={<Navigate to="/team" replace />} />
            <Route path="/clientregistration"    element={<ProtectedRoute permKey="clients">       <ClientRegistration />    </ProtectedRoute>} />
            <Route path="/therapistregistration" element={<ProtectedRoute permKey="therapists">    <TherapistRegistration /> </ProtectedRoute>} />
            <Route path="/edit-profile"          element={<ProtectedRoute>                         <EditProfile />           </ProtectedRoute>} />
            <Route path="/resources"             element={<ProtectedRoute permKey="resources">     <ResourcesPage />         </ProtectedRoute>} />
            <Route path="/intake"                element={<ProtectedRoute permKey="clients">       <IntakePage />            </ProtectedRoute>} />
            <Route path="/chat-users"            element={<ProtectedRoute permKey="clients">       <ChatUsersPage />         </ProtectedRoute>} />
            <Route path="/email-logs"            element={<ProtectedRoute permKey="clients">       <EmailLogsPage />         </ProtectedRoute>} />
            <Route path="/certificates"          element={<ProtectedRoute permKey="certificates">  <CertificatesPage />      </ProtectedRoute>} />
            <Route path="/probono"               element={<ProtectedRoute permKey="probono">       <ProbonoPage />           </ProtectedRoute>} />
            <Route path="/internship-applications" element={<ProtectedRoute permKey="probono">     <InternshipApplicationsPage /> </ProtectedRoute>} />
            <Route path="/client-360" element={<ProtectedRoute permKey="clients"><Client360Page /></ProtectedRoute>} />
            <Route path="/client-360/:id" element={<ProtectedRoute permKey="clients"><Client360Page /></ProtectedRoute>} />
            <Route path="/session-packs" element={<ProtectedRoute permKey="bookings"><SessionPacksPage /></ProtectedRoute>} />
            <Route path="/pack-pricing" element={<ProtectedRoute permKey="bookings"><PackPricingPage /></ProtectedRoute>} />
            <Route path="/todo" element={<ProtectedRoute><TodoPage /></ProtectedRoute>} />
            <Route path="/trainee-portal" element={<ProtectedRoute permKey="probono"><TraineePortalPage /></ProtectedRoute>} />
            <Route path="/reports"               element={<ProtectedRoute permKey="reports">       <ReportsPage />           </ProtectedRoute>} />
            <Route path="/quotations"            element={<ProtectedRoute permKey="quotations">    <QuotationsPage />        </ProtectedRoute>} />
            <Route path="/offer-letters"         element={<ProtectedRoute permKey="offerLetters">  <OfferLettersPage />      </ProtectedRoute>} />
            <Route path="/letterheads"            element={<ProtectedRoute permKey="letterheads">   <LetterheadsPage />        </ProtectedRoute>} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <ToastContainer position="top-right" autoClose={3000} />
    </ThemeProvider>
  );
}

export default App;
